@if (mobileMode$ | async) {
  <!-- Mobile Mode: Full width, no sidebar styling -->
  <div class="lesson-map-mobile h-full bg-white flex flex-col overflow-hidden">
    <!-- Header -->
    <div class="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
      <div class="flex items-center justify-between">
        <h3 class="text-xs font-semibold text-gray-400 uppercase tracking-widest flex items-center gap-2">
          <div class="w-6 h-6 rounded-lg bg-brand/10 flex items-center justify-center">
            <lucide-icon [img]="icons.Map" class="w-3.5 h-3.5 text-brand"></lucide-icon>
          </div>
          Lesson Plan
        </h3>
        <button
          (click)="editRequested.emit()"
          class="text-xs text-brand hover:text-brand/80 font-medium flex items-center gap-1.5 transition-colors duration-200 cursor-pointer"
        >
          <lucide-icon [img]="icons.Edit" class="w-3 h-3"></lucide-icon>
          Edit
        </button>
      </div>
    </div>

    <!-- Timeline Content -->
    <div class="flex-1 overflow-y-auto py-3 px-3 custom-scrollbar">
      @if (viewData$ | async; as data) {
        <div class="space-y-3">
          @for (block of data.plan; track block.id) {
            <div class="block-wrapper">
              <!-- Block Header -->
              <div class="block-card py-2.5 px-3 rounded-lg">
                <span class="text-sm font-semibold block text-gray-600">
                  {{ block.phase }}
                </span>
                <p class="text-xs text-gray-400 truncate">
                  {{ block.activity }}
                </p>
              </div>

              <!-- Nested Slides -->
              @if (data.slidesByBlock.get(block.id); as blockSlides) {
                <div class="slides-list mt-1.5 space-y-1 pl-2">
                  @for (item of blockSlides; track item.index) {
                    <div
                      class="slide-item flex items-center gap-2 py-2 px-2.5 rounded-lg cursor-pointer transition-all duration-200 active:scale-[0.98]"
                      [class.bg-brand]="isSlideActive(item.index)"
                      [class.text-white]="isSlideActive(item.index)"
                      [class.shadow-sm]="isSlideActive(item.index)"
                      [class.hover:bg-gray-50]="!isSlideActive(item.index)"
                      [class.bg-gray-50]="!isSlideActive(item.index)"
                      [class.text-gray-600]="!isSlideActive(item.index)"
                      (click)="onSlideClick(item.index, block.id, $event)"
                    >
                      <!-- Slide Thumbnail -->
                      <div class="shrink-0 w-12 h-7 rounded overflow-hidden border border-gray-200 bg-white">
                        @if (getThumbnailForSlide(item.index, data.thumbnails); as thumbnail) {
                          <img 
                            [src]="thumbnail.thumbnailUrl" 
                            alt="Slide {{ item.index + 1 }}"
                            class="w-full h-full object-cover"
                            loading="lazy"
                          />
                        }
                      </div>
                      <span class="text-sm flex-1 font-medium leading-tight">
                        {{ item.index + 1 }}. {{ getSlideTitle(item.slide) }}
                      </span>
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>
      }
    </div>
  </div>
} @else {
  <!-- Desktop Mode: Sidebar styling -->
  <div
    class="lesson-map h-full bg-white border-r border-gray-100 w-72 shrink-0 flex flex-col overflow-hidden shadow-sm"
  >
    <!-- Header -->
    <div class="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
      <h3 class="text-xs font-semibold text-gray-400 uppercase tracking-widest flex items-center gap-2.5">
        <div class="w-6 h-6 rounded-lg bg-brand/10 flex items-center justify-center">
          <lucide-icon [img]="icons.Map" class="w-3.5 h-3.5 text-brand"></lucide-icon>
        </div>
        Lesson Plan
      </h3>
      <button
        (click)="editRequested.emit()"
        class="mt-2 text-xs text-brand hover:text-brand/80 font-medium flex items-center gap-1.5 transition-colors duration-200 cursor-pointer"
      >
        <lucide-icon [img]="icons.Edit" class="w-3 h-3"></lucide-icon>
        Click here to edit lesson plan
      </button>
    </div>

    <!-- Timeline Content -->
    <div class="flex-1 overflow-y-auto py-4 px-2 custom-scrollbar">
      @if (viewData$ | async; as data) {
        <div class="timeline-container relative">
          <!-- Timeline line: positioned at center of point -->
          <div class="timeline-line absolute left-[33px] top-5 bottom-0 w-0.5 bg-gradient-to-b from-gray-200 via-gray-200 to-transparent"></div>

          @for (block of data.plan; track block.id; let i = $index; let isLast = $last) {
            <div class="block-wrapper mb-1">
              <!-- Timeline Row -->
              <div class="flex items-center">
                <!-- Fixed width container for time + point (w-10 = 40px) -->
                <div class="w-10 shrink-0 flex items-center">
                  <!-- Time label: takes remaining space, right-aligned, truncated -->
                  <span class="text-[9px] font-medium text-gray-400 flex-1 text-right truncate pr-2">
                    {{ getBlockStartTime(data.plan, i) }}m
                  </span>
                  <!-- Point: fixed 12px -->
                  <div
                    class="w-3 h-3 rounded-full border-2 border-white shadow-sm transition-all duration-300 z-10 shrink-0"
                    [class.bg-brand]="data.activeBlockId === block.id"
                    [class.ring-4]="data.activeBlockId === block.id"
                    [class.ring-brand/20]="data.activeBlockId === block.id"
                    [class.bg-gray-300]="data.activeBlockId !== block.id"
                  ></div>
                </div>

                <!-- Block Content -->
                <div
                  #blockRef
                  class="block-card flex-1 py-2.5 px-2 rounded-xl transition-all duration-300 min-w-0"
                >
                  <span class="text-sm font-semibold truncate block mb-0.5 text-gray-600">
                    {{ block.phase }}
                  </span>
                  <p class="text-xs text-gray-400">
                    {{ block.activity }}
                  </p>
                </div>
              </div>

              <!-- Nested Slides -->
              @if (data.slidesByBlock.get(block.id); as blockSlides) {
                <div class="slides-list ml-10 mt-1 space-y-1">
                  @for (item of blockSlides; track item.index; let j = $index) {
                    <div
                      class="slide-item flex items-start gap-2 py-1.5 px-2 rounded-lg cursor-pointer transition-all duration-200"
                      [class.bg-brand]="isSlideActive(item.index)"
                      [class.text-white]="isSlideActive(item.index)"
                      [class.shadow-sm]="isSlideActive(item.index)"
                      [class.ring-2]="isSlideActive(item.index)"
                      [class.ring-brand/30]="isSlideActive(item.index)"
                      [class.hover:bg-gray-100]="!isSlideActive(item.index)"
                      [class.text-gray-600]="!isSlideActive(item.index)"
                      (click)="onSlideClick(item.index, block.id, $event)"
                    >
                      <!-- Slide Thumbnail -->
                      <div class="shrink-0 w-10 h-6 rounded overflow-hidden border border-gray-200 bg-gray-50">
                        @if (getThumbnailForSlide(item.index, data.thumbnails); as thumbnail) {
                          <img 
                            [src]="thumbnail.thumbnailUrl" 
                            alt="Slide {{ item.index + 1 }}"
                            class="w-full h-full object-cover"
                            loading="lazy"
                          />
                        }
                      </div>
                      <span class="text-xs flex-1 font-medium leading-4">
                        {{ item.index + 1 }}. {{ getSlideTitle(item.slide) }}
                      </span>
                    </div>
                  }
                </div>
              }
            </div>
          }

          <!-- End of Lesson -->
          <div class="mt-3 flex items-center">
            <!-- Same fixed width container -->
            <div class="w-10 shrink-0 flex items-center">
              <span class="text-[9px] font-medium text-gray-300 flex-1 text-right truncate pr-2">
                {{ getBlockStartTime(data.plan, data.plan.length) }}m
              </span>
              <div class="w-2.5 h-2.5 rounded-full bg-gray-200 border-2 border-white shadow-sm shrink-0"></div>
            </div>
            <span class="text-xs font-medium text-gray-300 pl-2">End</span>
          </div>
        </div>
      }
    </div>
  </div>
}
